import { Coupon } from "./coupons";
import { LoyaltyBonus } from "./loyalty";

export enum CustomersFilterTypes {
  firstName = "firstName",
  lastName = "lastName",
  phoneNumber = "phoneNumber",
  lastOrderDate = "lastOrderDate",
  mostOrderValue = "mostOrderValue",
  createdAtAfter = "createdAtAfter",
  loyaltyLevel = "loyaltyLevel",
  isVip = "isVip",
  isColdKitchen = "isColdKitchen",
  isAiEnabled = "isAiEnabled",
  isCaloriesBankEnabled = "isCaloriesBankEnabled",
}

export enum CustomersFilterNames {
  firstName = "First name",
  lastName = "Last name",
  phoneNumber = "Phone number",
  lastOrderDate = "Last order date",
  mostOrderValue = "Most order value",
  createdAtAfter = "Created after",
  loyaltyLevel = "Loyalty level",
  isVip = "Status",
  isColdKitchen = "Cold kitchen",
  isAiEnabled = "AI features",
  isCaloriesBankEnabled = "Calories bank",
}

export enum Gender {
  Male = "male",
  Female = "female",
}

export enum CustomerGoal {
  Lose = "lose",
  Maintain = "maintain",
  Gain = "gain",
}

export enum CustomerActivityLevelGoal {
  Sedentary = "sedentary",
  Light_activity = "light_activity",
  Moderate_activity = "moderate_activity",
  Very_active = "very_active",
}

export type CustomerHealthData = {
  weight: number;
  height: number;
  goal: CustomerGoal;
  activityLevel: CustomerActivityLevelGoal;
  perWeekWeightChange: CustomerActivityLevelGoal;
  updatedAt: string;
};

export type CustomerHealthCalculations = {
  tdee: number;
  bmr: number;
  recommendedDailyCaloriesIntake: number;
  updatedAt: string;
};

export type CustomerProfileResponseDto = {
  id: string;
  couponUsageCount: number;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  email: string | null;
  gender: Gender | null;
  birthDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  healthData: CustomerHealthData | null;
  healthCalculations: CustomerHealthCalculations | null;
  isHealthDataQuestionnaireSkipped: boolean;
  isAIAssistantEnabled: boolean;
  isCaloriesBankEnabled: boolean;
  emailVerified: boolean;
  referralsCount: number;
  isVip: boolean;
  isColdKitchen: boolean;
  fullName?: string | null;
  balanceData: {
    fmcCentsAmount: number | null;
    expiresAt: string | null;
  };
  loyaltyData?: {
    bonus?: LoyaltyBonus & { isAvailable: boolean };
    birthdayGift?: number;
    healthyPerkAvailable?: boolean;
    discountPercentage?: number;
    freeDeliveryDays?: number;
    id?: string;
    name?: string;
    order?: number;
    pointsAmountForOrder?: number;
  };
};

export type GetCustomersResponseDto = {
  totalCount: number;
  items: CustomerProfileResponseDto[];
};

export type CustomerMappedProfile = CustomerProfileResponseDto & {
  fullName: string | null;
};

export type GetCustomersMappedResponseDto = {
  totalCount: number;
  items: CustomerMappedProfile[];
};

export type CustomerBalanceResponseDto = {
  fmcCentsAmount: number | null;
  expiresAt: string | null;
};

export type GetCustomersAddressResponseDto = {
  additionalInfo: string;
  area: string;
  block: string;
  building: string;
  city: string;
  createdAt: string;
  flat: string;
  floor: string;
  id: string;
  isInAnyDeliveryZone: boolean;
  isSelected: boolean;
  lat: number;
  lng: number;
  name: string;
  phone: string;
  street: string;
  updatedAt: string;
};
export type NutritionInfo = {
  carbs: number;
  protein: number;
  fat: number;
};

export interface ComboGroupSubItem {
  _domainEvents: unknown[];
  id: string;
  createdAt: string;
  updatedAt: string;
  category: unknown;
  deliverectId: string;
  deliverectPLU: string;
  description: string;
  imageURL: string;
  menu: string;
  subProducts: unknown[];
  name: string;
  fiatCentsPrice: number;
  discountPercentages: number;
  discountPrice: number;
  snooze: unknown;
  tags: unknown[];
  highlights: unknown[];
  alergents: unknown[];
  labels: unknown[];
  type: string;
  modifierType: unknown;
  min: number;
  max: number;
  multiMax: number | null;
  calories: number;
  externalFiatCentsPrice: number;
  isActive: boolean;
  isVip: boolean;
  isColdKitchen: boolean;
  isTopLevelProduct: boolean;
  isNewArrival: boolean;
  carbs: number;
  protein: number;
  fat: number;
  productStatus: unknown;
  availableTimeSlots: unknown[];
  fmcCentsPrice: number;
  isSnoozed: boolean;
}

export type ComboGroup = {
  _domainEvents: unknown[];
  id: string;
  createdAt: string;
  updatedAt: string;
  category: unknown;
  deliverectId: string;
  deliverectPLU: string;
  description: {
    en: string;
    ar: string;
  };
  imageURL: string | null;
  menu: string;
  subProducts: string[];
  name: { en: string | null; ar: string | null };
  fiatCentsPrice: number;
  snooze: unknown;
  tags: unknown[];
  highlights: unknown[];
  alergents: unknown[];
  labels: unknown[];
  type: string;
  modifierType: unknown;
  min: number;
  max: number;
  multiMax: unknown;
  calories: unknown;
  externalFiatCentsPrice: number;
  isActive: boolean;
  isVip: boolean;
  isColdKitchen: boolean;
  isTopLevelProduct: boolean;
  isNewArrival: boolean;
  carbs: unknown;
  protein: unknown;
  fat: unknown;
  productStatus: unknown;
  availableTimeSlots: unknown[];
  subItems: ComboGroupSubItem[];
};

export type CustomerMenuAddonModifier = {
  id: string;
  name: string;
  description: string;
  fiatCentsPrice: number;
  fmcCentsPrice: number;
  type: string;
  min: number;
  max: number;
  multiMax: number | null;
  calories: number | null;
  imageURL: string | null;
  tags: string[] | null;
  nutritionInfo?: NutritionInfo | null;
  isSnoozed: boolean;
  carbs?: number | null;
  protein?: number | null;
  fat?: number | null;
  quantity?: number;
};

export type CustomerMenuProduct = {
  id: string;
  name: string;
  description: string;
  productStatus: string | null;
  imageURL: string | null;
  fiatCentsPrice: number;
  fmcCentsPrice: number;
  min: number;
  max: number;
  multiMax: null;
  isSnoozed: boolean;
  addons: CustomerMenuAddonModifier[];
  modifiers: CustomerMenuAddonModifier[];
  calories: number;
  carbs: number;
  protein: number;
  fat: number;
  tags: string[];
  highlights: unknown[];
  type: "product";
  createdAt: string;
  updatedAt: string;
  subProducts: string[];
  providerId: string;

  comboGroups?: ComboGroup[];
};

export type CustomerMenuCategory = {
  id: string;
  name: string;
  description: string;
  imageURL: string | null;
  createdAt: string;
  updatedAt: string;
  products: CustomerMenuProduct[];
};

export type GetCustomerMenuResponseDto = {
  locationId: string;
  locationOperatingStatus: string;
  id: string;
  name: string;
  description: string;
  availabilities: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
  }[];
  isAvailable: boolean;
  isCashPayment: boolean;
  isCashPaymentAvailable: boolean;
  createdAt: string;
  updatedAt: string;
  categories: CustomerMenuCategory[];
};

export type GetCustomerCouponsResponseDto = {
  items: Coupon[];
  totalCount: number;
};
